import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChange,
} from '@angular/core';

import { deepEqual } from 'fast-equals';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ListSortableItemComponent,
  SortableItem,
} from '../list-sortable-item/list-sortable-item.component';

export const ListSortableComponentAction = {
  REORDER: 'orderChange',
  DELETE: 'deleteItem',
  RENAME: 'renameItem',
};

@Component({
  selector: 'rt-list-sortable',
  templateUrl: './list-sortable.component.html',
  styleUrls: ['./list-sortable.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkDropList, ListSortableItemComponent, CdkDrag, AsyncPipe],
})
export class ListSortableComponent {
  @Input() public items: SortableItem[] = [];
  @Input() public actions: any[] = [];

  // sets 'renamable' flag to true on every item unless it has 'renamable' flag explicitly set to false
  @Input()
  public set allowItemsRename(val: boolean) {
    this.itemsFlags.renamable = val;
  }

  // sets 'removable' flag to true on every item unless it has 'removable' flag explicitly set to false
  @Input()
  public set allowItemsRemove(val: boolean) {
    this.itemsFlags.removable = val;
  }

  // emits action name, items list, optionally item on which action occurred
  @Output()
  public action: EventEmitter<{
    name: string;
    item: SortableItem | null;
    items: SortableItem[];
  }> = new EventEmitter();
  // should update items in memory, without waiting for action result
  @Input() public immediate: boolean = false;

  public processing: boolean = false;

  private queue$: BehaviorSubject<SortableItem[]> = new BehaviorSubject([]);
  private itemsFlags: { renamable: boolean; removable: boolean } = {
    renamable: false,
    removable: false,
  };

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngOnChanges({ items }: { items: SimpleChange }): void {
    if (items && !items.firstChange) {
      const newItems = items.currentValue || [];
      const queuedList = this.queue$.getValue();

      if (queuedList.length) {
        const updatedIds = queuedList
          .filter(queuedItem => {
            const updatedItem = newItems.find((item: SortableItem) => {
              if (item.id === queuedItem.id) {
                return item;
              }
              return false;
            });
            return !deepEqual(queuedItem, updatedItem);
          })
          .map(i => i.id);
        if (updatedIds.length) {
          this.queue$.next(queuedList.filter(q => updatedIds.indexOf(q.id) === -1));
          this.cdRef.detectChanges();
        }
      }
    }
  }

  public onDrop(event: CdkDragDrop<SortableItem>): void {
    const { previousIndex, currentIndex } = event;
    const newItems = [...this.items];

    moveItemInArray(newItems, previousIndex, currentIndex);
    this.items = newItems;
    this.cdRef.detectChanges();
    this.emitAction(ListSortableComponentAction.REORDER);
  }

  public delete(item: any): void {
    if (this.immediate) {
      this.items = this.items.filter(cached => cached.id !== item.id);
    }
    this.emitAction(ListSortableComponentAction.DELETE, item);
  }

  public nameChanged(item: any, newName: any): void {
    if (this.immediate) {
      this.items = this.items.map(cached => {
        if (cached === item.id) {
          item.name = newName;
        }
        return item;
      });
    } else {
      this.addToProcessingQueue(item);
    }

    this.emitAction(
      ListSortableComponentAction.RENAME,
      this.immediate ? item : { ...item, name: newName },
    );
  }

  public isAllowedTo(flagName: 'renamable' | 'removable', item: SortableItem): boolean {
    // @ts-ignore
    if (item[flagName] === true || item[flagName] === false) {
      // @ts-ignore
      return item[flagName];
    }

    return this.itemsFlags[flagName];
  }

  public addToProcessingQueue(item: SortableItem): void {
    this.queue$.next(this.queue$.getValue().concat(item));
  }

  public isProcessing$(item: SortableItem): Observable<boolean> {
    return this.queue$
      .asObservable()
      .pipe(
        map(items => !!items.find(cached => cached.id === item.id && !deepEqual(cached, item))),
      );
  }

  public trackItem(index: number, item: SortableItem): number | string | void {
    return item.id || item.name || item.title;
  }

  private emitAction(name: string, item: SortableItem | null = null): void {
    this.action.emit({ name, item, items: this.items });
  }
}
